import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Plus, Trash2, Edit2, Check, User, Mail, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { customerAPI } from '../../services/api';

const FamilyMembersTab = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    relationship: '',
    email: '',
    phone: '',
    can_book: true,
    notes: ''
  });

  const relationships = ['Spouse', 'Parent', 'Child', 'Sibling', 'Grandparent', 'Other'];

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await customerAPI.getFamily();
      setMembers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch family members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await customerAPI.updateFamilyMember(editingId, formData);
        toast.success('Family member updated');
      } else {
        await customerAPI.addFamilyMember(formData);
        toast.success('Family member added');
      }
      fetchMembers();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (member) => {
    setFormData({
      full_name: member.full_name,
      relationship: member.relationship,
      email: member.email || '',
      phone: member.phone || '',
      can_book: member.can_book,
      notes: member.notes || ''
    });
    setEditingId(member.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this family member?')) return;
    try {
      await customerAPI.deleteFamilyMember(id);
      setMembers(members.filter(m => m.id !== id));
      toast.success('Family member removed');
    } catch (error) {
      toast.error('Failed to remove family member');
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      relationship: '',
      email: '',
      phone: '',
      can_book: true,
      notes: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getRelationshipColor = (rel) => {
    const colors = {
      'Spouse': 'bg-pink-100 text-pink-700',
      'Parent': 'bg-[#D4AF37]/10 text-[#D4AF37]',
      'Child': 'bg-[var(--success)]/15 text-green-700',
      'Sibling': 'bg-[#F4C2C2]/15 text-purple-700',
      'Grandparent': 'bg-amber-100 text-amber-700',
    };
    return colors[rel] || 'bg-[var(--background-subtle)] text-[var(--text-primary)]';
  };

  if (loading) {
    return (
      <div className="card p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading family members...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Users className="w-6 h-6 text-[var(--primary)]" />
            Family Members
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Add household members who may need services or can book on your behalf
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="btn-primary" data-testid="add-family-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Member
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card p-6 mb-6 border-2 border-[var(--primary)]"
          >
            <h3 className="font-bold mb-4">{editingId ? 'Edit Family Member' : 'Add Family Member'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="John Doe"
                    required
                    data-testid="family-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Relationship *</label>
                  <select
                    value={formData.relationship}
                    onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                    className="input-styled w-full"
                    required
                    data-testid="family-relationship"
                  >
                    <option value="">Select relationship</option>
                    {relationships.map(rel => (
                      <option key={rel} value={rel}>{rel}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                    data-testid="family-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="family-phone"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="can_book"
                  checked={formData.can_book}
                  onChange={(e) => setFormData({ ...formData, can_book: e.target.checked })}
                  className="w-4 h-4 rounded border-[var(--border-primary)]"
                />
                <label htmlFor="can_book" className="text-sm">Can book services on my behalf</label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any special notes about this family member..."
                  rows={2}
                  data-testid="family-notes"
                />
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="btn-primary" data-testid="save-family-btn">
                  <Check className="w-4 h-4 mr-2" />
                  {editingId ? 'Update' : 'Add'} Member
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Members List */}
      {members.length === 0 && !showForm ? (
        <div className="card p-12 text-center">
          <Users className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">No Family Members Added</h3>
          <p className="text-[var(--text-secondary)] mb-4">
            Add family members to easily book services for them or allow them to book on your account.
          </p>
          <Button onClick={() => setShowForm(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add First Member
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {members.map((member) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="card p-5"
                data-testid={`family-${member.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-[var(--accent)] flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-[var(--primary)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold truncate">{member.full_name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getRelationshipColor(member.relationship)}`}>
                        {member.relationship}
                      </span>
                    </div>
                    {member.email && (
                      <p className="text-sm text-[var(--text-muted)] flex items-center gap-1 mt-1">
                        <Mail className="w-3 h-3" />
                        {member.email}
                      </p>
                    )}
                    {member.phone && (
                      <p className="text-sm text-[var(--text-muted)] flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {member.phone}
                      </p>
                    )}
                    {member.can_book && (
                      <span className="text-xs text-[var(--success)] mt-2 inline-block">
                        Can book services
                      </span>
                    )}
                    {member.notes && (
                      <p className="text-xs text-[var(--text-muted)] mt-2 italic">"{member.notes}"</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-[var(--border-primary)]">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEdit(member)}
                    data-testid={`edit-family-${member.id}`}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDelete(member.id)}
                    className="text-[var(--error)] hover:text-red-700 hover:border-red-300"
                    data-testid={`delete-family-${member.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default FamilyMembersTab;
